/**
 * BL-75 (D-210) · Touch-first placement — the thin DOM binding (SPEC §8/§9).
 *
 * The ONLY layer that touches the DOM. It wires Pointer Events on the board AND the armory
 * chips, owns the cached board rect (refreshed on resize/orientation, never per-move), applies
 * the touch guardrails (`touch-action:none` etc.), and forwards intents to the DOM-agnostic
 * `PlacementController`. It REPLACES the legacy canvas `mousemove`/`mouseleave`/`click`
 * (removed in `inputHandlers`) so a touch tap places exactly once — the browser's synthetic
 * `click` after `pointerup` is harmless (the board no longer listens for `click`).
 *
 * `setPointerCapture` routes a gesture's move/up/cancel back to the grab element, so a
 * rail→board drag (and an off-board release = cancel) tracks. iOS edge-swipe is mitigated by
 * a PWA/Capacitor shell (Phase 2).
 */
import { PlacementController } from "./PlacementController.ts";
import { clientToCell, type RectLike } from "./clientToCell.ts";
import {
  PLACEMENT_TUNING,
  HAPTIC_PATTERNS,
  type HapticKind,
} from "./placementTuning.ts";
import type { MouseState } from "../game/inputHandlers.ts";
import type { PlaceableDescriptor, PreviewPort } from "./placementTypes.ts";
import { isReducedMotion } from "../engine/MotionSettings.ts";

export type ChipArm = (type: string) => void;

export interface BindPlacementDeps {
  /** The board canvas. */
  canvas: HTMLCanvasElement;
  /** Shared aim-state the renderer reads. */
  mouse: MouseState;
  preview: PreviewPort;
  getArmed: () => PlaceableDescriptor | null;
  /** select / firewall-sell / PT-17 deselect — returns true if it consumed the press. */
  tryHandleNonPlacement: (col: number, row: number) => boolean;
  setHoveredTower: (col: number, row: number) => void;
  clearHoveredTower: () => void;
  /** Arm the placeable a chip's `data-type` names (sets gs + the chip visual). Always
   *  arms — idempotent, never toggles (the toggle decision lives in the controller,
   *  made at UP once tap-vs-drag is known — D-332 amendment). */
  arm: ChipArm;
  /** Is the given chip type ALREADY the armed placeable? Read at DOWN, before `arm`
   *  runs, so the controller can decide at UP whether a motionless press was a
   *  genuine tap-to-disarm. */
  isArmed: (type: string) => boolean;
  /** Disarm the current placeable (a genuine chip tap on its own already-armed type). */
  disarm: () => void;
  /** gs.lives > 0 && phase is BUILD/COMBAT — gates the place/select path (not hover). */
  isInteractive: () => boolean;
  /** True while a payload is being aimed (the existing tap-to-target flow, Phase-2 conversion). */
  payloadActive: () => boolean;
  /** Resolve the existing payload tap-to-target at the release point. */
  resolvePayload: (clientX: number, clientY: number) => void;
}

type PointerKind = "mouse" | "touch" | "pen";

const CHIP_SELECTOR = ".tower-btn";

function chipOf(target: unknown): HTMLElement | null {
  const el = target as { closest?: (s: string) => Element | null } | null;
  return (el?.closest?.(CHIP_SELECTOR) as HTMLElement | null) ?? null;
}

/** Progressive-enhancement haptics — fire only where the Vibration API exists + motion is on. */
function fireHaptic(kind: HapticKind): void {
  if (typeof navigator === "undefined") return;
  const nav = navigator as Navigator & { vibrate?: (p: number | number[]) => boolean };
  if (typeof nav.vibrate !== "function") return;
  if (isReducedMotion()) return;
  nav.vibrate(HAPTIC_PATTERNS[kind]);
}

/**
 * Bind the unified pointer pipeline. Returns the controller so the host can wire
 * `Escape → onCancel` into its existing keydown handler (coexisting with payload-Esc / pause).
 */
export function bindPlacementPointer(deps: BindPlacementDeps): PlacementController {
  const { canvas } = deps;
  const tuning = PLACEMENT_TUNING;

  // ── cached board rect (SPEC §5): never getBoundingClientRect per pointermove ──
  let cachedRect: RectLike | null = null;
  const refreshRect = (): void => {
    cachedRect = canvas.getBoundingClientRect();
  };
  const getRect = (): RectLike | null => {
    if (!cachedRect) refreshRect();
    return cachedRect;
  };
  const invalidateRect = (): void => {
    cachedRect = null;
  };
  window.addEventListener("resize", invalidateRect);
  window.addEventListener("orientationchange", invalidateRect);
  window.visualViewport?.addEventListener("resize", invalidateRect);

  const controller = new PlacementController({
    mouse: deps.mouse,
    preview: deps.preview,
    getArmed: deps.getArmed,
    clientToCell: (x, y, kind) => clientToCell(x, y, kind, getRect(), tuning),
    cacheRect: refreshRect,
    isChip: (target) => chipOf(target) != null,
    armChip: (target) => {
      const type = chipOf(target)?.dataset["type"];
      if (type) deps.arm(type);
    },
    isChipArmed: (target) => {
      const type = chipOf(target)?.dataset["type"];
      return type ? deps.isArmed(type) : false;
    },
    disarmChip: () => deps.disarm(),
    tryHandleNonPlacement: deps.tryHandleNonPlacement,
    setHoveredTower: deps.setHoveredTower,
    clearHoveredTower: deps.clearHoveredTower,
    haptic: fireHaptic,
    tuning,
  });

  const kindOf = (e: PointerEvent): PointerKind =>
    (e.pointerType as PointerKind) || "mouse";

  // The pointer that owns the active carry. A second finger during a drag is ignored for aim
  // (SPEC §10) — only this pointer's move/up/cancel drive the carry.
  let activePointerId: number | null = null;
  const isStrayPointer = (e: PointerEvent): boolean =>
    controller.isCarrying() && e.pointerId !== activePointerId;

  // F-16 (device risk): iOS can background the app mid-carry WITHOUT ever emitting
  // `pointercancel` (the OS just suspends the page). Left unguarded, `carrying` stays
  // true with a stale `activePointerId` and EVERY subsequent press is refused — a hard
  // input lockout that only a reload clears. Cancel any in-flight carry on the three
  // signals that can precede/accompany a suspend: `visibilitychange` (tab/app hidden),
  // `blur` (window loses focus), `pagehide` (navigation/teardown).
  const cancelCarryOnBackground = (): void => {
    if (controller.isCarrying()) {
      controller.onCancel();
      activePointerId = null;
    }
  };
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) cancelCarryOnBackground();
  });
  window.addEventListener("blur", cancelCarryOnBackground);
  window.addEventListener("pagehide", cancelCarryOnBackground);

  function bindGrabSource(el: HTMLElement): void {
    el.addEventListener("pointerdown", (e) => {
      if (e.button > 0) {
        controller.onCancel(); // right/middle click cancels a carry (works mid-drag)
        activePointerId = null;
        return;
      }
      if (controller.isCarrying()) return; // a carry owns the gesture → ignore extra fingers
      if (!deps.isInteractive()) return; // dead / wrong phase — ignore the press
      if (deps.payloadActive()) return; // payload tap resolves on pointerup
      const started = controller.onDown(
        { x: e.clientX, y: e.clientY },
        kindOf(e),
        e.target,
      );
      if (started) {
        activePointerId = e.pointerId;
        if (tuning.POINTER_CAPTURE) {
          (el as HTMLElement & {
            setPointerCapture?: (id: number) => void;
          }).setPointerCapture?.(e.pointerId);
        }
      }
    });

    el.addEventListener("pointermove", (e) => {
      if (isStrayPointer(e)) return; // ignore a 2nd finger's moves during a carry
      controller.onMove({ x: e.clientX, y: e.clientY }, kindOf(e));
    });

    el.addEventListener("pointerup", (e) => {
      (el as HTMLElement & {
        releasePointerCapture?: (id: number) => void;
      }).releasePointerCapture?.(e.pointerId);
      if (isStrayPointer(e)) return; // ignore a 2nd finger's release during a carry
      if (deps.payloadActive()) {
        if (deps.isInteractive()) deps.resolvePayload(e.clientX, e.clientY);
        activePointerId = null;
        return;
      }
      // Dead / wrong phase mid-drag → cancel the carry cleanly (never commit, never get stuck).
      if (!deps.isInteractive()) {
        controller.onCancel();
        activePointerId = null;
        return;
      }
      controller.onUp({ x: e.clientX, y: e.clientY }, kindOf(e));
      activePointerId = null;
    });

    el.addEventListener("pointercancel", (e) => {
      if (isStrayPointer(e)) return;
      controller.onCancel();
      activePointerId = null;
    });
    el.addEventListener("pointerleave", (e) => controller.onHoverEnd(kindOf(e)));
  }

  // Touch guardrails (SPEC §8): the browser must not steal the drag as scroll/pinch, and iOS
  // must not pop a selection/callout/loupe mid-drag — on the board AND the (scrollable) chips.
  const applyTouchGuards = (el: HTMLElement): void => {
    el.style.touchAction = "none";
    el.style.userSelect = "none";
    el.style.setProperty("-webkit-touch-callout", "none");
    el.style.setProperty("-webkit-user-select", "none");
  };

  applyTouchGuards(canvas);
  bindGrabSource(canvas);

  // Grab can ALSO start on a chip in the rail (chips are NOT board children); capture then
  // tracks the rail→board drag. Re-query is cheap and done once at bind time.
  const chips = document.querySelectorAll<HTMLElement>(CHIP_SELECTOR);
  chips.forEach((chip) => {
    applyTouchGuards(chip);
    bindGrabSource(chip);
  });

  return controller;
}
